import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProject } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function ProjectAchievementListPage() {
  const { businessDomainId, businessUnitId, projectId, goalId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals/${goalId}`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [goal, setGoal] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchProject(projectId);
      // goalId は配列インデックス
      const foundGoal = (d.goals || [])[Number(goalId)];
      setGoal(foundGoal || null);
      // 実績はプロジェクトレベルの achievements から対象指標名でフィルタリング
      const filtered = (d.achievements || []).filter(
        (a) => a.goal?.indicator?.indicatorName === foundGoal?.indicator?.indicatorName
      );
      setRows(filtered.map((a, i) => ({ id: i, ...a })));
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, goalId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredRows = searchText
    ? rows.filter((r) => r.indicatorName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "indicatorName", headerName: t("project.goal.indicator"), flex: 1, minWidth: 150,
      valueGetter: (v, row) => row?.goal?.indicator?.indicatorName || "" },
    { field: "plannedValue", headerName: t("project.achievement.plannedValue"), width: 110,
      valueGetter: (v, row) => row?.goal?.plannedValue || "" },
    { field: "actualValue", headerName: t("project.achievement.actualValue"), width: 110 },
    { field: "unit", headerName: t("project.goal.unit"), width: 80,
      valueGetter: (v, row) => row?.goal?.indicator?.unit || "" },
    { field: "actualDate", headerName: t("project.achievement.actualDate"), width: 120, valueFormatter: (v) => formatDate(v) },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("project.achievement.list.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/achievements/create`)}>{t("common.add")}</Button>
      </Box>
      <DataGrid
        rows={filteredRows} columns={columns} getRowId={(r) => r.id}
        loading={loading} autoHeight pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={(p) => navigate(`${basePath}/achievements/${p.row.id}/edit`)}
        disableRowSelectionOnClick sx={{ cursor: "pointer" }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
